//@ts-nocheck
import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  Menu,
  MenuItem,
} from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import QualificationGroup from "./QualificationGroup";
import {
  QualificationGroupType,
  QualificationSubGroup as QualificationSubGroupType,
  QualificationLevel,
  QualificationItem,
} from "./types";
import { useGetToken } from "../../hooks/useGetToken";
import axios, { AxiosRequestConfig } from "axios";
import Loading from "../../components/Loading";
import { useGlobalStateContext } from "../../context/globalState";
import Context from "../../context";
import PassportPageBottom from "../PassportPageBottom";
import PassportPageHeader from "../PassportPageHeader";
import AlertBox from "../AlertBox";

/**
 * Recalculates the status message fraction based on filtered items.
 * Only counts non-notvalid items in the denominator.
 */
const recalcStatusMessage = (
  childStatuses: string[],
  originalMessage?: string,
): string => {
  if (childStatuses.length === 0) return originalMessage || "";
  const total = childStatuses.length;
  const completed = childStatuses.filter((s) =>
    ["valid", "warning"].includes(s),
  ).length;
  const hasIncomplete =
    childStatuses.includes("notqualified") ||
    childStatuses.includes("needaction");
  if (hasIncomplete) return i18n.t("passportPage.labels.needAction");
  if (total > 0 && completed === total)
    return i18n.t("passportPage.labels.completedCount", { completed, total });
  if (total > 0) return i18n.t("passportPage.labels.completedCount", { completed, total });
  return i18n.t("passportPage.labels.notCompleted");
};

/**
 * Recursively filters out notvalid (N/A) items from a subgroup/level.
 * Returns null if the subgroup becomes empty after filtering.
 */
const filterSubGroup = (
  sub: QualificationSubGroupType,
): QualificationSubGroupType | null => {
  const filteredItems = sub.items?.filter((i) => i.status !== "notvalid") || [];
  const filteredLevels = (sub.levels
    ?.map((level: QualificationLevel) => {
      const levelItems = level.items.filter((i) => i.status !== "notvalid");
      if (levelItems.length === 0) return null;
      const levelStatuses = levelItems.map((i) => i.status);
      return {
        ...level,
        items: levelItems,
        statusMessage: recalcStatusMessage(levelStatuses, level.statusMessage),
      };
    })
    .filter(Boolean) || []) as QualificationLevel[];
  const filteredSubGroups = (sub.subGroups
    ?.map((s) => filterSubGroup(s))
    .filter(Boolean) || []) as QualificationSubGroupType[];

  if (
    filteredItems.length === 0 &&
    filteredLevels.length === 0 &&
    filteredSubGroups.length === 0
  )
    return null;

  // Collect child statuses for recalculating this level's message
  const childStatuses = [
    ...filteredItems.map((i) => i.status),
    ...filteredLevels.map((l) => l.status),
    ...filteredSubGroups.map((s) => s.status || ""),
  ].filter(Boolean);

  return {
    ...sub,
    items: filteredItems,
    levels: filteredLevels,
    subGroups: filteredSubGroups,
    statusMessage: recalcStatusMessage(childStatuses, sub.statusMessage),
  };
};

/**
 * Filters out notvalid (N/A) trainings from the entire qualification list.
 * Removes empty rollup levels that have no remaining children.
 */
const filterNotValid = (
  qualifications: QualificationGroupType[],
  t: (key: string, fallback?: string) => string,
): QualificationGroupType[] => {
  return qualifications
    .map((group) => {
      const filteredItems =
        group.items?.filter((item) => item.status !== "notvalid") || [];
      const filteredSubGroups = (group.subGroups
        ?.map((sub) => filterSubGroup(sub))
        .filter(Boolean) || []) as QualificationSubGroupType[];

      if (filteredItems.length === 0 && filteredSubGroups.length === 0)
        return null;

      // Only recalculate statusMessage if children were actually filtered out
      const originalCount =
        (group.items?.length || 0) + (group.subGroups?.length || 0);
      const filteredCount = filteredItems.length + filteredSubGroups.length;

      let statusMessage = group.statusMessage;
      if (filteredCount < originalCount) {
        // Groups split by country (Local Requirements, Global Methods) have the
        // parent message "X/Y Country|Countries" - other groups end with
        // "Completed". Detect by the country label suffix, NOT by structure
        // (every group can have empty items + subGroups + an "X/Y" prefix).
        const countrySingular = t("passportPage.labels.country", "Country");
        const countryPlural = t("passportPage.labels.countries", "Countries");
        const original = group.statusMessage || "";
        const isCountryGrouped =
          original.endsWith(` ${countrySingular}`) ||
          original.endsWith(` ${countryPlural}`);
        if (isCountryGrouped) {
          const total = filteredSubGroups.length;
          const completed = filteredSubGroups.filter((s) =>
            ["valid", "warning"].includes(s.status || ""),
          ).length;
          const label = total === 1 ? countrySingular : countryPlural;
          statusMessage = `${completed}/${total} ${label}`;
        } else {
          const childStatuses = [
            ...filteredItems.map((i) => i.status),
            ...filteredSubGroups.map((s) => s.status || ""),
          ].filter(Boolean);
          statusMessage = recalcStatusMessage(
            childStatuses,
            group.statusMessage,
          );
        }
      }

      return {
        ...group,
        items: filteredItems,
        subGroups: filteredSubGroups,
        statusMessage,
      };
    })
    .filter(Boolean) as QualificationGroupType[];
};

const PassportQualificationForm: React.FC = ({ onSignOut }) => {
  const {
    authState: { user },
  } = useContext(Context);

  const { t } = useTranslation();

  const {
    supplierType,
    updateAuthState,
    loading,
    beginLoading,
    endLoading,
    qualificationsList,
    setQualificationsList,
    trainingTypes,
    userPreference: { languagePreference },
  } = useGlobalStateContext();

  const [getToken] = useGetToken();
  const [data, setData] = useState([]);
  const [error, setError] = useState<string | null>(null);
  const [loadingData, setLoadingData] = useState(false);
  const [hideNA, setHideNA] = useState(true);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [passportType, setPassportType] = useState("");
  const [businessType, setBusinessType] = useState("");

  const hasFetchedRef = useRef(false);

  const navigate = useNavigate();

  /**
   * shouldRedirectToProfilePage - If user is Inactive or Blocked, they should be redirected to profile page to see the reason
   * @param userStatus - String Active, Inactive or Blocked
   * @returns boolean - true if user should be redirected to profile page to update details, false otherwise
   */
  const shouldRedirectToProfilePage = (userStatus: string) => {
    const normalizedStatus = userStatus.toLowerCase();
    return normalizedStatus === "inactive" || normalizedStatus === "blocked";
  };

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const fetchData = async () => {
      const loadingToken = beginLoading("passport-qual-installer");
      const getQueryParams = () => {
        const params = new URLSearchParams();
        if (user?.username) {
          params.set("emailId", user.username);
          if (!user.username.trim().toLowerCase().endsWith("@kone.com")) {
            params.set("sub", "true");
          }
        } else {
          params.set("userName", user?.name ?? "");
          params.set("sub", "true");
        }

        return `?${params.toString()}`;
      };
      try {
        const token = await getToken();
        const uri = `${process.env.REACT_APP_API_ENDPOINT}/v1/installers/view${getQueryParams()}`;

        const config: AxiosRequestConfig = {
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        };

        const response = await axios.get(uri, config);
        const installer = response.data?.installer?.[0];
        if (installer) {
          if (shouldRedirectToProfilePage(installer.status)) {
            navigate("/profile");
            return;
          }
          const userId = installer.userId;
          setPassportType(installer.passportTypes?.[0] || "");
          setBusinessType(installer.businessTypes?.[0]?.name || "");
          await fetchUserQualifications(userId);
        }

        console.log(
          "User logged in at (UTC):",
          "true",
          installer?.countryId,
          new Date().toISOString(),
        );
      } catch (err) {
        setError(t("error_messages.noAccessContactSupervisor"));
      } finally {
        endLoading(loadingToken);
      }
    };

    fetchData();
  }, []);

  const fetchUserQualifications = async (userId: any) => {
    const loadingToken = beginLoading("passport-qual-qualifications");
    const token = await getToken();

    const uri = `${process.env.REACT_APP_API_ENDPOINT}/v1/trainings/passportQualifications?userId=${userId}&lang=${languagePreference}`;
    const config: AxiosRequestConfig = {
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
    };
    try {
      const response = await axios.get(uri, config);
      const userQualificationsResponse = response.data;
      // const formattedQualifications = formatUserQualificationResponse(userQualificationsResponse[0].qualifications)
      setQualificationsList(userQualificationsResponse.qualifications);
    } catch (err) {
      setError(t("error_messages.noAccessContactSupervisor"));
    } finally {
      endLoading(loadingToken);
    }
  };
  console.log("qualificationsList", qualificationsList);

  function onCloseAlertBox() {
    setError(null);
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      height="100vh"
      sx={{ pb: 8 }}
    >
      <Card
        sx={{
          maxWidth: 500,
          borderRadius: 2,
          boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)",
          backgroundColor: "#FFFFFF",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <PassportPageHeader onSignOut={onSignOut} />
        {loading && <Loading />}
        <CardContent
          sx={{ p: 2, backgroundColor: "#FFFFFF", flex: 1, overflowY: "auto" }}
        >
          {error && (
            <div
              style={{
                marginTop: "20px",
                marginBottom: "10px",
                position: "sticky",
                left: "0",
                zIndex: 200,
              }}
            >
              <AlertBox
                type={"error"}
                message={error}
                onCloseCb={onCloseAlertBox}
              />
            </div>
          )}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
            sx={{ backgroundColor: "#FFFFFF" }}
          >
            <Typography variant="h6" fontWeight="bold">
              {passportType
                ? passportType.toLowerCase() === "work"
                  ? businessType
                    ? `${t("passportPage.labels.passportTitle.work", "WORK")} - ${t("businessTypes." + businessType, businessType)}`
                    : t("passportPage.labels.passportTitle.work", "WORK")
                  : passportType.toLowerCase() === "entry"
                    ? t("passportPage.labels.passportTitle.entry", "ENTRY")
                    : t("passportPage.subTitle")
                : t("passportPage.subTitle")}
            </Typography>
            <IconButton onClick={(e) => setFilterAnchorEl(e.currentTarget)}>
              <FilterAltOutlinedIcon />
            </IconButton>
            <Menu
              anchorEl={filterAnchorEl}
              open={Boolean(filterAnchorEl)}
              onClose={() => setFilterAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  borderRadius: "8px",
                  border: "1px solid #E2EBFC",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  minWidth: 160,
                },
              }}
            >
              <MenuItem
                onClick={() => {
                  setHideNA(false);
                  setFilterAnchorEl(null);
                }}
                sx={{
                  fontSize: "0.875rem",
                  py: 1,
                  backgroundColor: !hideNA ? "#F1F4FD" : "transparent",
                  "&:hover": { backgroundColor: "#F1F4FD" },
                }}
              >
                {t("passportPage.labels.filter.showAll", "Show All")}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setHideNA(true);
                  setFilterAnchorEl(null);
                }}
                sx={{
                  fontSize: "0.875rem",
                  py: 1,
                  backgroundColor: hideNA ? "#F1F4FD" : "transparent",
                  "&:hover": { backgroundColor: "#F1F4FD" },
                }}
              >
                {t("passportPage.labels.filter.hideNA", "Hide N/A")}
              </MenuItem>
            </Menu>
          </Box>

          <Box display="flex" fontWeight="bold" mb={1} px={1}>
            <Box flex={1}>{t("passportPage.labels.tableHeader")}</Box>
            <Box>{t("passportPage.labels.tableSubHeader2")}</Box>
          </Box>

          {(hideNA
            ? filterNotValid(qualificationsList, t)
            : qualificationsList
          ).map((group, i) => (
            <QualificationGroup key={i} group={group} />
          ))}
        </CardContent>
      </Card>
      <PassportPageBottom />
    </Box>
  );
};

export default PassportQualificationForm;
